import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Grid, TextField, MenuItem, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Divider } from '@mui/material';
import { ArrowBack, Save, AccountBalance } from '@mui/icons-material';
import api from '../../services/api';

const statusLabels = {
  entwurf: { label: 'Entwurf', color: 'default' },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'info' },
  fertig: { label: 'Fertig', color: 'success' },
  eingereicht: { label: 'Eingereicht', color: 'primary' },
};

const SachberichtDetailPage = () => {
  const { projektId, berichtId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nachweis, setNachweis] = useState(null);
  const [nachweisLoading, setNachweisLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/sachberichte/${berichtId}`);
      setForm(res.data);
    } catch (err) { setError('Fehler beim Laden'); }
  }, [berichtId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      await api.put(`/sachberichte/${berichtId}`, {
        titel: form.titel,
        typ: form.typ,
        status: form.status,
        berichtszeitraumVon: form.berichtszeitraumVon,
        berichtszeitraumBis: form.berichtszeitraumBis,
        einreichungsfrist: form.einreichungsfrist,
        inhaltZiele: form.inhaltZiele,
        inhaltMassnahmen: form.inhaltMassnahmen,
        inhaltErgebnisse: form.inhaltErgebnisse,
        inhaltBewertung: form.inhaltBewertung,
        inhaltAusblick: form.inhaltAusblick,
        anzahlVeranstaltungen: form.anzahlVeranstaltungen,
        anzahlTeilnehmende: form.anzahlTeilnehmende,
        notizen: form.notizen
      });
      setSuccess('Gespeichert!');
    } catch (err) { setError('Fehler beim Speichern'); }
  };

  const loadNachweis = async () => {
    setNachweisLoading(true);
    try {
      const res = await api.get(`/sachberichte/${berichtId}/verwendungsnachweis`);
      setNachweis(res.data);
    } catch (err) { setError('Fehler beim Laden des Verwendungsnachweises'); }
    setNachweisLoading(false);
  };

  const formatCurrency = (val) => parseFloat(val || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (!form) return <Box sx={{ p: 3 }}><Typography>Laden...</Typography></Box>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/sachberichte/${projektId}`)}
          sx={{ color: '#1B5E20' }}>
          Zurück
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="#1B5E20">{form.titel}</Typography>
          <Typography variant="body2" color="text.secondary">{form.projekt?.titel}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave}
          sx={{ bgcolor: '#1B5E20', '&:hover': { bgcolor: '#2E7D32' } }}>
          Speichern
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Inhalt links */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="#1B5E20" gutterBottom>Berichtsinhalte</Typography>

              <TextField fullWidth label="Titel" value={form.titel || ''}
                onChange={(e) => update('titel', e.target.value)} margin="normal" />

              <TextField fullWidth label="Was waren die Ziele?" value={form.inhaltZiele || ''}
                onChange={(e) => update('inhaltZiele', e.target.value)} margin="normal" multiline rows={4}
                placeholder="Beschreiben Sie die Ziele des Berichtszeitraums..." />

              <TextField fullWidth label="Welche Maßnahmen wurden durchgeführt?" value={form.inhaltMassnahmen || ''}
                onChange={(e) => update('inhaltMassnahmen', e.target.value)} margin="normal" multiline rows={4}
                placeholder="Beschreiben Sie die durchgeführten Maßnahmen..." />

              <TextField fullWidth label="Was wurde erreicht?" value={form.inhaltErgebnisse || ''}
                onChange={(e) => update('inhaltErgebnisse', e.target.value)} margin="normal" multiline rows={4}
                placeholder="Beschreiben Sie die erzielten Ergebnisse..." />

              <TextField fullWidth label="Bewertung der Ergebnisse" value={form.inhaltBewertung || ''}
                onChange={(e) => update('inhaltBewertung', e.target.value)} margin="normal" multiline rows={4}
                placeholder="Wie bewertet die IAN die Ergebnisse?" />

              <TextField fullWidth label="Ausblick" value={form.inhaltAusblick || ''}
                onChange={(e) => update('inhaltAusblick', e.target.value)} margin="normal" multiline rows={4}
                placeholder="Wie geht es weiter?" />

              <TextField fullWidth label="Notizen" value={form.notizen || ''}
                onChange={(e) => update('notizen', e.target.value)} margin="normal" multiline rows={2} />
            </CardContent>
          </Card>
        </Grid>

        {/* Metadaten rechts */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="#1B5E20" gutterBottom>Metadaten</Typography>

              <TextField fullWidth select label="Typ" value={form.typ || 'zwischenbericht'}
                onChange={(e) => update('typ', e.target.value)} margin="normal" size="small">
                <MenuItem value="zwischenbericht">Zwischenbericht</MenuItem>
                <MenuItem value="abschlussbericht">Abschlussbericht</MenuItem>
              </TextField>

              <TextField fullWidth select label="Status" value={form.status || 'entwurf'}
                onChange={(e) => update('status', e.target.value)} margin="normal" size="small">
                {Object.entries(statusLabels).map(([key, val]) =>
                  <MenuItem key={key} value={key}>{val.label}</MenuItem>
                )}
              </TextField>

              <TextField fullWidth label="Zeitraum von" type="date" value={form.berichtszeitraumVon || ''}
                onChange={(e) => update('berichtszeitraumVon', e.target.value)} margin="normal" size="small"
                InputLabelProps={{ shrink: true }} />

              <TextField fullWidth label="Zeitraum bis" type="date" value={form.berichtszeitraumBis || ''}
                onChange={(e) => update('berichtszeitraumBis', e.target.value)} margin="normal" size="small"
                InputLabelProps={{ shrink: true }} />

              <TextField fullWidth label="Einreichungsfrist" type="date" value={form.einreichungsfrist || ''}
                onChange={(e) => update('einreichungsfrist', e.target.value)} margin="normal" size="small"
                InputLabelProps={{ shrink: true }} />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight="bold" color="#1B5E20" gutterBottom>Kennzahlen</Typography>

              <TextField fullWidth label="Anzahl Veranstaltungen" type="number"
                value={form.anzahlVeranstaltungen || 0}
                onChange={(e) => update('anzahlVeranstaltungen', parseInt(e.target.value) || 0)}
                margin="normal" size="small" />

              <TextField fullWidth label="Anzahl Teilnehmende" type="number"
                value={form.anzahlTeilnehmende || 0}
                onChange={(e) => update('anzahlTeilnehmende', parseInt(e.target.value) || 0)}
                margin="normal" size="small" />

              {form.ersteller && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Erstellt von: {form.ersteller.firstName} {form.ersteller.lastName}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Verwendungsnachweis */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="#1B5E20">Verwendungsnachweis</Typography>
            <Button variant="outlined" startIcon={<AccountBalance />} onClick={loadNachweis}
              disabled={nachweisLoading}
              sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
              {nachweisLoading ? 'Laden...' : 'Finanzübersicht laden'}
            </Button>
          </Box>

          {nachweis && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, bgcolor: '#E8F5E9', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Gesamtbudget</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#1B5E20">{formatCurrency(nachweis.gesamtbudget)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Ausgaben</Typography>
                    <Typography variant="h6" fontWeight="bold">{formatCurrency(nachweis.istGesamt)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Verbleibend</Typography>
                    <Typography variant="h6" fontWeight="bold" color={nachweis.rest < 0 ? '#D32F2F' : '#1B5E20'}>
                      {formatCurrency(nachweis.rest)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Kostenpositionen */}
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#E8F5E9' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Kostenposition</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Soll (EUR)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Ist (EUR)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Differenz (EUR)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nachweis.positionen.map((pos, idx) => (
                      <TableRow key={pos.id || idx} hover>
                        <TableCell>{pos.bezeichnung}</TableCell>
                        <TableCell align="right">{formatCurrency(pos.sollBetrag)}</TableCell>
                        <TableCell align="right">{formatCurrency(pos.istBetrag)}</TableCell>
                        <TableCell align="right" sx={{ color: pos.differenz < 0 ? '#D32F2F' : 'inherit' }}>
                          {formatCurrency(pos.differenz)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(nachweis.gesamtbudget)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(nachweis.istGesamt)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: nachweis.rest < 0 ? '#D32F2F' : 'inherit' }}>
                        {formatCurrency(nachweis.rest)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Belegliste */}
              <Typography variant="subtitle1" fontWeight="bold" color="#1B5E20" sx={{ mb: 1 }}>Belege</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#E8F5E9' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Beleg-Nr.</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Beschreibung</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Kostenposition</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Betrag</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nachweis.positionen.flatMap(pos =>
                      (pos.belege || []).map(b => (
                        <TableRow key={b.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{b.belegnummer}</TableCell>
                          <TableCell>{formatDate(b.datum)}</TableCell>
                          <TableCell>{b.beschreibung}</TableCell>
                          <TableCell>{pos.bezeichnung}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(b.betrag)}</TableCell>
                          <TableCell>
                            <Chip label={b.status} size="small"
                              color={b.status === 'freigegeben' ? 'success' : b.status === 'geprueft' ? 'info' : 'default'} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {nachweis.positionen.every(p => (p.belege || []).length === 0) && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#999' }}>Keine Belege vorhanden</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SachberichtDetailPage;
